/**
 * i18n des thèmes astraux, côté client.
 *
 * Le thème est calculé côté serveur avec les enums en clés stables anglaises (SUN, TAURUS,
 * CONJUNCTION, FIRE…) : neutres pour la roue `.sky-chart` (qui indexe ses glyphes dessus) et pour
 * l'API. Avant d'envoyer ce JSON à un template Mustache d'étude (prompt LLM), on le localise ici —
 * chaque valeur ou clé d'enum astro devient son libellé dans la langue de la page.
 *
 * Dictionnaire plat : les noms d'enums astro sont globalement uniques (aucune collision entre
 * signes, astres, aspects, éléments, modalités), donc un simple remplacement par valeur suffit,
 * sans connaître la forme du thème. Ajouter une langue = une entrée de plus, rien d'autre à toucher.
 *
 * On ne localise QUE les valeurs, jamais les clés d'objet : les templates lisent les balances par
 * leur clé anglaise (ex. `{{elementalBalance.FIRE}}` pour en tirer le compte) — renommer la clé en
 * « Feu » les casserait. Les valeurs de balance sont d'ailleurs des nombres, rien à traduire côté
 * clé. Volontairement NON traduits aussi : les maisons (chiffres romains I–XII, déjà neutres) et les
 * PatternType (pas de libellés définis côté serveur) — ils traversent tels quels.
 *
 * Source des libellés : arcate-module-astro/.../i18n/messages-astro_{fr,en}.properties (astro.*).
 * Doublon assumé de cette table côté client (termes ultra-stables) pour rester 100 % navigateur.
 */

export const ASTRO_LABELS = {
	fr: {
		// Signes
		ARIES: 'Bélier', TAURUS: 'Taureau', GEMINI: 'Gémeaux', CANCER: 'Cancer',
		LEO: 'Lion', VIRGO: 'Vierge', LIBRA: 'Balance', SCORPIO: 'Scorpion',
		SAGITTARIUS: 'Sagittaire', CAPRICORN: 'Capricorne', AQUARIUS: 'Verseau', PISCES: 'Poissons',
		// Éléments
		FIRE: 'Feu', EARTH: 'Terre', AIR: 'Air', WATER: 'Eau',
		// Modalités
		CARDINAL: 'Cardinal', FIXED: 'Fixe', MUTABLE: 'Mutable',
		// Astres et points
		SUN: 'Soleil', MOON: 'Lune', MERCURY: 'Mercure', VENUS: 'Vénus', MARS: 'Mars',
		JUPITER: 'Jupiter', SATURN: 'Saturne', URANUS: 'Uranus', NEPTUNE: 'Neptune', PLUTO: 'Pluton',
		NORTH_NODE: 'Nœud Nord', SOUTH_NODE: 'Nœud Sud', BLACK_MOON: 'Lune Noire',
		PART_OF_FORTUNE: 'Part de Fortune', ASCENDANT: 'Ascendant', MIDHEAVEN: 'Milieu du Ciel',
		DESCENDANT: 'Descendant', IMUM_COELI: 'Fond du Ciel',
		// Aspects
		CONJUNCTION: 'Conjonction', OPPOSITION: 'Opposition', TRINE: 'Trigone', SQUARE: 'Carré',
		SEXTILE: 'Sextile', SEMISEXTILE: 'Semi-sextile', QUINCUNX: 'Quinconce', SEMISQUARE: 'Semi-carré',
		SESQUISQUARE: 'Sesqui-carré', QUINTILE: 'Quintile', BIQUINTILE: 'Bi-quintile',
		SEPTILE: 'Septile', NOVILE: 'Novile',
	},
	en: {
		ARIES: 'Aries', TAURUS: 'Taurus', GEMINI: 'Gemini', CANCER: 'Cancer',
		LEO: 'Leo', VIRGO: 'Virgo', LIBRA: 'Libra', SCORPIO: 'Scorpio',
		SAGITTARIUS: 'Sagittarius', CAPRICORN: 'Capricorn', AQUARIUS: 'Aquarius', PISCES: 'Pisces',
		FIRE: 'Fire', EARTH: 'Earth', AIR: 'Air', WATER: 'Water',
		CARDINAL: 'Cardinal', FIXED: 'Fixed', MUTABLE: 'Mutable',
		SUN: 'Sun', MOON: 'Moon', MERCURY: 'Mercury', VENUS: 'Venus', MARS: 'Mars',
		JUPITER: 'Jupiter', SATURN: 'Saturn', URANUS: 'Uranus', NEPTUNE: 'Neptune', PLUTO: 'Pluto',
		NORTH_NODE: 'North Node', SOUTH_NODE: 'South Node', BLACK_MOON: 'Black Moon',
		PART_OF_FORTUNE: 'Part of Fortune', ASCENDANT: 'Ascendant', MIDHEAVEN: 'Midheaven',
		DESCENDANT: 'Descendant', IMUM_COELI: 'Imum Coeli',
		CONJUNCTION: 'Conjunction', OPPOSITION: 'Opposition', TRINE: 'Trine', SQUARE: 'Square',
		SEXTILE: 'Sextile', SEMISEXTILE: 'Semi-sextile', QUINCUNX: 'Quincunx', SEMISQUARE: 'Semi-square',
		SESQUISQUARE: 'Sesquiquadrate', QUINTILE: 'Quintile', BIQUINTILE: 'Biquintile',
		SEPTILE: 'Septile', NOVILE: 'Novile',
	},
};

/**
 * Remplace récursivement, dans un arbre JSON déjà parsé, toute valeur-chaîne qui est un nom d'enum
 * astro par son libellé (`dict` = `ASTRO_LABELS[lang]`). Seules les valeurs sont touchées (ex.
 * `sign: "TAURUS"` → `sign: "Taureau"`) ; les clés d'objet sont préservées telles quelles pour ne
 * pas casser les templates qui lisent les balances par clé anglaise (`{{elementalBalance.FIRE}}`).
 * Le reste (nombres, booléens, dates, chiffres romains des maisons) traverse intact.
 */
export function localizeAstroTree(node, dict) {
	if (Array.isArray(node)) {
		return node.map((item) => localizeAstroTree(item, dict));
	}
	if (node && typeof node === 'object') {
		const out = {};
		for (const [key, value] of Object.entries(node)) {
			out[key] = localizeAstroTree(value, dict);
		}
		return out;
	}
	if (typeof node === 'string' && Object.prototype.hasOwnProperty.call(dict, node)) {
		return dict[node];
	}
	return node;
}

/**
 * Localise une chaîne JSON de thème dans la langue donnée (code court, ex. "fr", "en"). Langue
 * inconnue ⇒ chaîne renvoyée telle quelle (les clés anglaises restent lisibles par un LLM). JSON
 * invalide ⇒ renvoyé tel quel (on ne casse jamais l'envoi).
 */
export function localizeAstroJson(json, lang) {
	const dict = ASTRO_LABELS[lang];
	if (!dict) {
		return json;
	}
	try {
		return JSON.stringify(localizeAstroTree(JSON.parse(json), dict));
	} catch (error) {
		return json;
	}
}
